import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import PremiumThemeSettings from './PremiumThemeSettings';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Settings,
  Crown,
  Shield,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Bell,
  BellOff,
  Mail,
  Phone,
  Home,
  LogOut,
  Edit,
  Save,
  X,
  Check,
  AlertCircle,
  Info,
  HelpCircle,
  ExternalLink,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  RefreshCw,
  RefreshCcw,
  RotateCw,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Move,
  Crop,
  Scissors,
  Paintbrush,
  Eraser,
  Pen,
  Pencil,
  Highlighter,
  Type,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Quote,
  Code,
  Terminal,
  Database,
  Server,
  Cloud,
  CloudOff,
  Wifi,
  WifiOff,
  Signal,
  SignalZero,
  SignalOne,
  SignalTwo,
  SignalThree,
  Battery,
  BatteryLow,
  BatteryMedium,
  BatteryHigh,
  BatteryFull,
  Plug,
  Unplug,
  Power,
  PowerOff,
  PlayCircle,
  PauseCircle,
  StopCircle,
  SkipBack,
  SkipForward,
  FastForward,
  Rewind,
  Shuffle,
  Repeat,
  RepeatOne,
  Volume1,
  Volume2,
  VolumeX,
  Headphones,
  Speaker,
  Mic,
  MicOff,
  Video,
  VideoOff,
  Camera,
  Image,
  File,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderMinus,
  FolderX,
  FilePlus,
  FileMinus,
  FileX,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FileCode,
  FileSpreadsheet,
  FilePdf,
  FileWord,
  FilePowerpoint,
  FileExcel,
  FileZip,
  FileJson,
  FileXml,
  FileCsv,
  FileTxt,
  FileMd,
  FileHtml,
  FileCss,
  FileJs,
  FileTs,
  FileJsx,
  FileTsx,
  FileVue,
  FileSvelte,
  FileAngular,
  FileReact,
  FileNode,
  FilePython,
  FileJava,
  FileC,
  FileCpp,
  FileCsharp,
  FilePhp,
  FileRuby,
  FileGo,
  FileRust,
  FileSwift,
  FileKotlin,
  FileDart,
  FileR,
  FileScala,
  FileHaskell,
  FileClojure,
  FileLua,
  FilePerl,
  FileBash,
  FilePowershell,
  FileYaml,
  FileToml,
  FileIni,
  FileEnv,
  FileGitignore,
  FileDocker,
  FileKubernetes,
  FileTerraform,
  FileAnsible,
  FileJenkins,
  FileGithub,
  FileGitlab,
  FileBitbucket,
  FileAzure,
  FileAws,
  FileGcp,
  FileDigitalocean,
  FileHeroku,
  FileVercel,
  FileNetlify,
  FileSurge,
  FileFirebase,
  FileSupabase,
  FilePlanetscale,
  FileMongodb,
  FilePostgresql,
  FileMysql,
  FileRedis,
  FileElasticsearch,
  FileKibana,
  FileGrafana,
  FilePrometheus,
  FileJaeger,
  FileZipkin,
  FileSentry,
  FileLogrocket,
  FileMixpanel,
  FileAmplitude,
  FileSegment,
  FileRudderstack,
  FileSnowflake,
  FileBigquery,
  FileRedshift,
  FileS3,
  FileCloudfront,
  FileRoute53,
  FileCloudflare,
  FileFastly,
  FileAkamai,
  FileMaxcdn,
  FileKeycdn,
  FileBunnycdn,
  FileStackpath,
  FileCloudinary,
  FileImgix,
  FileImagekit,
  FileUploadcare,
  FileFilestack,
  FileDropzone,
  FileTus,
  FileResumable,
  FileChunked,
  FileStreaming,
  FileProgressive,
  FileLazy,
  FileEager,
  FileOptimized,
  FileCompressed,
  FileMinified,
  FileBundled,
  FileTranspiled,
  FilePolyfilled,
  FileShimmed,
  FilePonyfilled,
  FileProlyfilled,
  Activity,
  Palette
} from 'lucide-react';

const ModernProfile = () => {
  const { user, logout, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [emailData, setEmailData] = useState({
    newEmail: '',
    password: ''
  });
  const [showSecuritySettings, setShowSecuritySettings] = useState(false);
  const [showThemeSettings, setShowThemeSettings] = useState(false);
  const [securityData, setSecurityData] = useState({
    twoFactorEnabled: false,
    loginNotifications: true,
    dataRetention: user?.data_retention_days || 7
  });
  
  // Premium Tema Ayarları
  const [themeSettings, setThemeSettings] = useState({
    // Arka Plan Ayarları
    backgroundType: 'gradient', // 'gradient', 'image', 'solid', 'pattern'
    backgroundColor: '#1e3a8a',
    backgroundGradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)',
    backgroundImage: '',
    backgroundPattern: 'none', // 'dots', 'grid', 'waves', 'geometric', 'none'
    backgroundOpacity: 0.9,
    
    // Renk Paleti
    primaryColor: '#1e3a8a',
    secondaryColor: '#3b82f6',
    accentColor: '#fbbf24',
    textColor: '#ffffff',
    cardColor: 'rgba(30, 58, 138, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.3)',
    
    // Yazı Tipi Ayarları
    fontFamily: 'Inter', // 'Inter', 'Roboto', 'Poppins', 'Montserrat', 'Open Sans', 'Lato'
    fontSize: 'medium', // 'small', 'medium', 'large', 'xlarge'
    fontWeight: 'normal', // 'light', 'normal', 'medium', 'semibold', 'bold'
    lineHeight: 'normal', // 'tight', 'normal', 'relaxed', 'loose'
    letterSpacing: 'normal', // 'tight', 'normal', 'wide'
    
    // Header Ayarları
    headerStyle: 'gradient', // 'gradient', 'solid', 'glass', 'minimal'
    headerColor: '#1e3a8a',
    headerGradient: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 50%, #1e3a8a 100%)',
    headerOpacity: 0.9,
    headerBlur: true,
    
    // Kart Ayarları
    cardStyle: 'glass', // 'glass', 'solid', 'outline', 'minimal'
    cardOpacity: 0.1,
    cardBlur: true,
    cardBorder: true,
    cardShadow: true,
    cardRadius: 'large', // 'none', 'small', 'medium', 'large', 'xl'
    
    // Buton Ayarları
    buttonStyle: 'gradient', // 'gradient', 'solid', 'outline', 'minimal'
    buttonRadius: 'medium', // 'none', 'small', 'medium', 'large', 'full'
    buttonShadow: true,
    buttonHover: true,
    
    // Animasyon Ayarları
    animations: true,
    animationSpeed: 'normal', // 'slow', 'normal', 'fast'
    hoverEffects: true,
    transitions: true,
    
    // Özel Efektler
    particles: false,
    particleType: 'dots', // 'dots', 'stars', 'snow', 'hearts'
    particleColor: '#fbbf24',
    particleCount: 50,
    
    // Gölge ve Efektler
    shadows: true,
    shadowIntensity: 'medium', // 'light', 'medium', 'strong'
    glowEffects: false,
    glowColor: '#3b82f6',
    
    // Özel CSS
    customCSS: ''
  });

  const tabs = [
    { id: 'profile', label: 'Profil', icon: User },
    { id: 'security', label: 'Güvenlik', icon: Shield },
    { id: 'preferences', label: 'Tercihler', icon: Settings },
    { id: 'theme', label: 'Tema', icon: Palette, premium: true },
    { id: 'billing', label: 'Faturalandırma', icon: Crown },
    { id: 'activity', label: 'Aktivite', icon: Activity }
  ];

  const themes = [
    { id: 'default', name: 'Varsayılan', color: 'bg-blue-500' },
    { id: 'dark', name: 'Karanlık', color: 'bg-gray-800' },
    { id: 'light', name: 'Açık', color: 'bg-white' },
    { id: 'purple', name: 'Mor', color: 'bg-purple-500' },
    { id: 'green', name: 'Yeşil', color: 'bg-green-500' }
  ];

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || ''
    });
  };

  const handleSave = async () => {
    try {
      // Update user data
      await updateUser(editData);
      setIsEditing(false);
    } catch (error) {
      console.error('Update error:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || ''
    });
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Yeni şifreler eşleşmiyor');
      return;
    }
    
    try {
      // Update password
      console.log('Password change requested');
      setShowPasswordChange(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Password change error:', error);
    }
  };

  const handleEmailChange = async () => {
    try {
      // Update email
      console.log('Email change requested');
      setShowEmailChange(false);
      setEmailData({ newEmail: '', password: '' });
    } catch (error) {
      console.error('Email change error:', error);
    }
  };

  const handleSecurityUpdate = async () => {
    try {
      // Update security settings
      console.log('Security settings updated');
      setShowSecuritySettings(false);
    } catch (error) {
      console.error('Security update error:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Tema ayarlarını uygula
  const applyThemeSettings = () => {
    const root = document.documentElement;
    
    // CSS değişkenlerini ayarla
    root.style.setProperty('--theme-primary', themeSettings.primaryColor);
    root.style.setProperty('--theme-secondary', themeSettings.secondaryColor);
    root.style.setProperty('--theme-accent', themeSettings.accentColor);
    root.style.setProperty('--theme-text', themeSettings.textColor);
    root.style.setProperty('--theme-card', themeSettings.cardColor);
    root.style.setProperty('--theme-border', themeSettings.borderColor);
    
    // Font ayarları
    root.style.setProperty('--theme-font-family', themeSettings.fontFamily);
    root.style.setProperty('--theme-font-size', themeSettings.fontSize);
    root.style.setProperty('--theme-font-weight', themeSettings.fontWeight);
    root.style.setProperty('--theme-line-height', themeSettings.lineHeight);
    root.style.setProperty('--theme-letter-spacing', themeSettings.letterSpacing);
    
    // Arka plan ayarları
    if (themeSettings.backgroundType === 'gradient') {
      root.style.setProperty('--theme-background', themeSettings.backgroundGradient);
    } else if (themeSettings.backgroundType === 'solid') {
      root.style.setProperty('--theme-background', themeSettings.backgroundColor);
    } else if (themeSettings.backgroundType === 'image') {
      root.style.setProperty('--theme-background', `url(${themeSettings.backgroundImage})`);
    }
    
    // Özel CSS uygula
    if (themeSettings.customCSS) {
      const styleId = 'custom-theme-css';
      let customStyle = document.getElementById(styleId);
      if (!customStyle) {
        customStyle = document.createElement('style');
        customStyle.id = styleId;
        document.head.appendChild(customStyle);
      }
      customStyle.textContent = themeSettings.customCSS;
    }
  };

  // Tema ayarlarını kaydet
  const saveThemeSettings = () => {
    localStorage.setItem('premium-theme-settings', JSON.stringify(themeSettings));
    applyThemeSettings();
    toast.success('Tema ayarları kaydedildi!');
  };

  // Tema ayarlarını yükle
  const loadThemeSettings = () => {
    const saved = localStorage.getItem('premium-theme-settings');
    if (saved) {
      setThemeSettings(JSON.parse(saved));
    }
  };

  // Bileşen yüklendiğinde tema ayarlarını yükle
  React.useEffect(() => {
    if (user?.is_premium) {
      loadThemeSettings();
    }
  }, [user?.is_premium]);

  const calculateStats = () => {
    if (!user) return { totalChats: 0, totalMessages: 0, totalImages: 0, totalVideos: 0 };
    
    return {
      totalChats: user.chats?.length || 0,
      totalMessages: user.messages_sent || 0,
      totalImages: user.images_generated || 0,
      totalVideos: user.videos_generated || 0
    };
  };

  const stats = calculateStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Profil Ayarları
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Hesap bilgilerinizi ve tercihlerinizi yönetin
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {user?.name || 'Kullanıcı'}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {user?.email || 'email@example.com'}
                  </p>
                </div>
              </div>

              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                      Profil Bilgileri
                    </h3>
                    {!isEditing && (
                      <button
                        onClick={handleEdit}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Düzenle</span>
                      </button>
                    )}
                  </div>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Ad Soyad
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editData.name}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        ) : (
                          <p className="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                            {user?.name || 'Belirtilmemiş'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Email
                        </label>
                        {isEditing ? (
                          <input
                            type="email"
                            value={editData.email}
                            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        ) : (
                          <p className="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                            {user?.email || 'Belirtilmemiş'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Telefon
                        </label>
                        {isEditing ? (
                          <input
                            type="tel"
                            value={editData.phone}
                            onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                        ) : (
                          <p className="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                            {user?.phone || 'Belirtilmemiş'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Kullanıcı Tipi
                        </label>
                        <p className="px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                          {user?.user_type === 'guest' ? 'Misafir' : 
                           user?.user_type === 'google' ? 'Google' : 
                           user?.user_type === 'email' ? 'Email' : 'Bilinmiyor'}
                        </p>
                      </div>
                    </div>

                    {isEditing && (
                      <div className="flex space-x-4">
                        <button
                          onClick={handleSave}
                          className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Save className="w-4 h-4" />
                          <span>Kaydet</span>
                        </button>
                        <button
                          onClick={handleCancel}
                          className="flex items-center space-x-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          <span>İptal</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                    Güvenlik Ayarları
                  </h3>

                  <div className="space-y-6">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                            Şifre Değiştir
                          </h4>
                          <p className="text-gray-600 dark:text-gray-400">
                            Hesabınızın güvenliği için düzenli olarak şifrenizi güncelleyin
                          </p>
                        </div>
                        <button
                          onClick={() => setShowPasswordChange(!showPasswordChange)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          {showPasswordChange ? 'İptal' : 'Değiştir'}
                        </button>
                      </div>

                      {showPasswordChange && (
                        <div className="mt-6 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Mevcut Şifre
                            </label>
                            <input
                              type="password"
                              value={passwordData.currentPassword}
                              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Yeni Şifre
                            </label>
                            <input
                              type="password"
                              value={passwordData.newPassword}
                              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Yeni Şifre Tekrar
                            </label>
                            <input
                              type="password"
                              value={passwordData.confirmPassword}
                              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                            />
                          </div>
                          <button
                            onClick={handlePasswordChange}
                            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            Şifreyi Güncelle
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                            Email Değiştir
                          </h4>
                          <p className="text-gray-600 dark:text-gray-400">
                            Hesap email adresinizi güncelleyin
                          </p>
                        </div>
                        <button
                          onClick={() => setShowEmailChange(!showEmailChange)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          {showEmailChange ? 'İptal' : 'Değiştir'}
                        </button>
                      </div>

                      {showEmailChange && (
                        <div className="mt-6 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Yeni Email
                            </label>
                            <input
                              type="email"
                              value={emailData.newEmail}
                              onChange={(e) => setEmailData({ ...emailData, newEmail: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Mevcut Şifre
                            </label>
                            <input
                              type="password"
                              value={emailData.password}
                              onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-600 dark:text-white"
                            />
                          </div>
                          <button
                            onClick={handleEmailChange}
                            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            Email'i Güncelle
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Preferences Tab */}
              {activeTab === 'theme' && (
          <PremiumThemeSettings 
            user={user} 
            onSave={(settings) => {
              console.log('Theme settings saved:', settings);
            }}
            onReset={(settings) => {
              console.log('Theme settings reset:', settings);
            }}
          />
        )}

        {activeTab === 'preferences' && (
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                    Tercihler
                  </h3>

                  <div className="space-y-6">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        Tema Seçimi
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {themes.map((themeOption) => (
                          <button
                            key={themeOption.id}
                            onClick={() => setTheme(themeOption.id)}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              theme === themeOption.id
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full mx-auto mb-2 ${themeOption.color}`}></div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {themeOption.name}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        Bildirimler
                      </h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">Email Bildirimleri</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Önemli güncellemeler hakkında email alın
                            </p>
                          </div>
                          <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600 transition-colors">
                            <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">Push Bildirimleri</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Tarayıcı bildirimleri alın
                            </p>
                          </div>
                          <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 dark:bg-gray-600 transition-colors">
                            <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Billing Tab */}
              {activeTab === 'billing' && (
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                    Faturalandırma
                  </h3>

                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-xl font-semibold">
                            {user?.is_premium ? 'Premium Aktif' : 'Ücretsiz Plan'}
                          </h4>
                          <p className="text-blue-100">
                            {user?.is_premium 
                              ? `Premium ${user?.premium_level || 'Basic'} planı aktif`
                              : 'Premium özellikler için yükseltin'
                            }
                          </p>
                        </div>
                        <Crown className="w-8 h-8 text-yellow-300" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                        <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Toplam Sohbet</h5>
                        <p className="text-3xl font-bold text-blue-600">{stats.totalChats}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                        <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Gönderilen Mesaj</h5>
                        <p className="text-3xl font-bold text-green-600">{stats.totalMessages}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                        <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Oluşturulan Görsel</h5>
                        <p className="text-3xl font-bold text-purple-600">{stats.totalImages}</p>
                      </div>
                    </div>

                    {!user?.is_premium && (
                      <div className="text-center">
                        <button className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105">
                          Premium'a Geç
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Activity Tab */}
              {activeTab === 'activity' && (
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                    Aktivite Geçmişi
                  </h3>

                  <div className="space-y-4">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Hesap oluşturuldu</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {user?.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Son giriş</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {user?.last_login ? new Date(user.last_login).toLocaleDateString('tr-TR') : 'Bilinmiyor'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernProfile;